import React from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import { FormattedMessage } from 'react-intl';
import { Button } from 'antd';
import Progress from './Progress';
import { colors } from '../../constants';
import Dropdown from '../common/Dropdown';



import {
  TooltipSpinner,
  Container,
  Title,
  Subtitle,
  Content,
  Background,
  SkipLink,
} from './styled';

const { Option } = Dropdown;

@inject('settingsStore')
@inject('onboardingStore')
@inject('sessionStore')
@inject('localeStore')
@observer
class Tooltip extends React.Component {
  constructor(props) {
    super(props);
    const { triggerMaximize } = props.onboardingStore;
    this.state = {
      wizardTransitioning: triggerMaximize,
    };
    this.startTransition = this.startTransition.bind(this);
    this.stopTransition = this.stopTransition.bind(this);
    this.handleNext = this.handleNext.bind(this);
  }

  componentDidMount() {
    this.maximizeWizard();
  }

  componentWillUnmount() {
    this.stopTransition();
    if (this.timerHandle) {
      clearTimeout(this.timerHandle);
      this.timerHandle = 0;
    }
  }

  handleNext(e, skipped = false) {
    const {
      onboardingStore,
      primaryProps,
      step: { storeActionBeforeNext = '' },
      index,
      sessionStore: { connectToUserFromToken },
    } = this.props;
    if (storeActionBeforeNext && !skipped) {
      onboardingStore[storeActionBeforeNext]();
    }
    onboardingStore.saveWizardState();
    if (skipped) {
      onboardingStore.enableNextWizard();
    }
    primaryProps.onClick(e);
    if (index === 3 || this.props.step.currentStage === 2.5) {
      connectToUserFromToken();
    }
  }

  handlePrevious(e) {
    const { onboardingStore, backProps } = this.props;
    if (!onboardingStore.nextWizardStepValid) {
      onboardingStore.enableNextWizard();
    }
    backProps.onClick(e);
  }

  startTransition(cb = () => {}) {
    this.setState(
      {
        wizardTransitioning: true,
      },
      cb,
    );
  }

  stopTransition(cb = () => {}) {
    this.setState(
      {
        wizardTransitioning: false,
      },
      cb,
    );
  }

  minimizeWizard() {
    this.startTransition(() => {
      this.timerHandle = setTimeout(() => {
        this.props.onboardingStore.toggleWizard();
        this.timerHandle = 0;
      }, 200);
    });
  }

  maximizeWizard() {
    const { toggleTriggerMaximize, triggerMaximize } = this.props.onboardingStore;
    if (triggerMaximize) {
      this.stopTransition(() => {
        toggleTriggerMaximize();
      });
    }
  }

  render() {
    const {
      title = '',
      subtitle = '',
      content = '',
      currentStage = 0,
      primaryButtonContent = 'Next',
      secondaryButtonContent = 'Previous',
      skipHeight = 0,
      bigContent = false,
      biggerContent = false,
      showProgress = false,
      showBackground = false,
      showSkip = false,
      forCourse = false,
      languageButton = false
    } = this.props.step;
    const { storeLoading, finish,isPaypalCardOption } = this.props.onboardingStore;
    const { isRtl } = this.props.settingsStore.userProfile;
    const { backProps, primaryProps, index } = this.props;
    const { wizardTransitioning } = this.state;
    if (storeLoading) {
      return (
        <Container>
          <TooltipSpinner className="loader" />
        </Container>
      );
    }
    const primaryButtonProps = {
      css: css`
        position: fixed;
        bottom: 21.4px;
        right: 37.8px;
        min-width: 88px;
        box-shadow: 0 3px 6px 0 ${colors.colorlessBoxShadow};
      `,
      ...primaryProps,
      type: 'primary',
      disabled: !this.props.onboardingStore.nextWizardStepValid,
      onClick: this.handleNext,
    };
    const buttonIsRenderedInContent = typeof content === 'function';

    return (
      <Container className={wizardTransitioning ? 'transitioning' : ''} bigger={biggerContent} forCourse={forCourse} isPaypalCardOption={isPaypalCardOption} isRtl={isRtl} >
        {showProgress ? 
          <div
            css={css`
              display:flex;
              justify-content:space-between;
            `}
          >
            <Progress current={currentStage} />
            <div
              onClick={()=>this.minimizeWizard()}
              css={css`
                margin-right:30px;
                font-size:20px;
                display:flex;
                align-items:center;
                cursor:pointer;
              `}
            >x
            </div>
          </div> :
          <div />}
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        {showBackground ? <Background big={bigContent} bigger={biggerContent} /> : null}
        <Content big={bigContent} bigger={biggerContent} forCourse={forCourse}>
          {buttonIsRenderedInContent ? content({ primaryButtonProps }) : content}
        </Content>
        <Button
          hidden={index !== 7 ?? false}
          type="primary"
          css={css`
            position: fixed;
            bottom: 21.4px;
            left: 60px;
            min-width: 88px;
            box-shadow: 0 3px 6px 0 ${colors.colorlessBoxShadow};
          `}
          onClick={() => {
            finish();
          }}
        >
          Finish
        </Button>
        {showSkip ? (
          <SkipLink onClick={e => this.handleNext(e, true)} skipHeight={skipHeight} />
        ) : null}
        {
          languageButton ? (
            <div
              css={css`
              position: fixed;
              bottom: 21.4px;
              right: 538.4px;
              min-width: 88px;
            `}
            >
              <Dropdown
                value={this.props.settingsStore.userProfile.languageId}
                onChange={(value)=>{
                  this.props.settingsStore.userProfile.setLanguageId(value)
                  this.props.localeStore.changeLanguage(value);
                }}
                width="150px"
              >
                <Option value="en">
                  <FormattedMessage id="userProfile.englishUSA" defaultMessage="English (USA)" />
                </Option>
                <Option value="he">
                  <FormattedMessage id="userProfile.hebrew" defaultMessage="עברית" />
                </Option>
              </Dropdown>
            </div>
          ) : null
        }
        {secondaryButtonContent ? (
          <Button
            css={css`
              position: fixed;
              bottom: 21.4px;
              right: 138.4px;
              min-width: 88px;
              box-shadow: 0 3px 6px 0 ${colors.colorlessBoxShadow};
            `}
            {...backProps}
            onClick={e => {this.handlePrevious(e)}}
          >
            {secondaryButtonContent}
          </Button>
        ) : null}
        {!buttonIsRenderedInContent ? (
          <Button
            {...primaryButtonProps}
          >
            {primaryButtonContent}
          </Button>
        ) : null}
      </Container>
    );
  }
}

Tooltip.propTypes = {
  continuous: PropTypes.bool,
  index: PropTypes.number,
  isLastStep: PropTypes.bool,
  size: PropTypes.number,
  step: PropTypes.object,
  backProps: PropTypes.object,
  closeProps: PropTypes.object,
  primaryProps: PropTypes.object,
  skipProps: PropTypes.object,
  tooltipProps: PropTypes.object,
  onboardingStore: PropTypes.object,
  settingsStore: PropTypes.object,
};

Tooltip.defaultProps = {};

export default Tooltip;
